import { useMemo } from 'react';
import { Link } from 'react-router';
import { berlinDate, dayBlocks, SHIFT_STATUS_LABELS, toMinutes, type Shift } from '@kontor/shared';
import { abbreviations, employeeHue, hoursDe } from './format';
import type { GridStep } from './gridStep';
import './shifts.css';

/** Zeilenhöhe je Takt in Pixel: gröberer Takt, kompaktere Darstellung (60 Minuten = 44 px, 30 = 28 px, 15 = 20 px) */
const ROW_HEIGHT: Record<GridStep, number> = { 60: 44, 30: 28, 15: 20 };
const pad = (n: number) => String(n).padStart(2, '0');
const label = (min: number) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;

interface Props {
  day: string;
  shifts: Shift[];
  /** Auswahl für Verwalter: Schicht anklicken, Kopf einer Person wählt ihre Schichten des Tages */
  selected?: ReadonlySet<string>;
  onToggle?: (id: string) => void;
  onToggleIds?: (ids: string[]) => void;
  /** Zeittakt der Zeilen in Minuten, Vorgabe viertelstündlich */
  step?: GridStep;
  /** Position je Mitarbeiter für den Hinweis am Namen */
  positions?: ReadonlyMap<string, string | null>;
  /** Der Name führt zur Mitarbeitermaske, nur mit dem Recht, Mitarbeiter anzusehen */
  linkEmployees?: boolean;
  /** Rechtsklick oder Kontextmenü-Taste auf einer Schicht öffnet das Menü der Seite */
  onMenu?: (shift: Shift, x: number, y: number) => void;
}

/** Koordinaten für das Kontextmenü: von der Maus, mit der Tastatur (ohne Mausposition) an der unteren linken Ecke des Blocks */
export const menuPoint = (e: React.MouseEvent<HTMLElement>) => {
  if (e.clientX || e.clientY) return { x: e.clientX, y: e.clientY };
  const r = e.currentTarget.getBoundingClientRect();
  return { x: r.left + 8, y: r.top + Math.min(r.height, 40) };
};

/**
 * Tagesansicht der Schichtplanung (KON-040): Eine Spalte je Mitarbeiter nebeneinander, die Zeilen sind die Uhrzeit in Schritten von 15 Minuten. Der sichtbare Bereich folgt
 * den Schichten des Tages (eine Stunde Luft davor und danach). So sieht man auf einen Blick, wer wann arbeitet und wo sich Schichten überschneiden oder Lücken entstehen.
 */
export function ShiftDayGrid({ day, shifts, selected, onToggle, onToggleIds, step = 15, positions, linkEmployees = false, onMenu }: Props) {
  const QUARTER = step;
  const selectable = !!onToggle;
  const isToday = day === berlinDate();
  const codes = useMemo(() => abbreviations(shifts), [shifts]);

  const people = useMemo(() => {
    const map = new Map<string, { id: string; name: string; shifts: Shift[] }>();
    for (const s of shifts) {
      const entry = map.get(s.employeeId) ?? { id: s.employeeId, name: s.employeeName, shifts: [] };
      entry.shifts.push(s);
      map.set(s.employeeId, entry);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [shifts]);

  const { startMin, rows } = useMemo(() => {
    if (shifts.length === 0) return { startMin: 6 * 60, rows: (14 * 60) / step };
    const first = Math.min(...shifts.map((s) => toMinutes(s.startTime)));
    const last = Math.max(...shifts.map((s) => toMinutes(s.endTime)));
    const from = Math.max(0, Math.floor(first / 60) * 60 - 60);
    const to = Math.min(24 * 60, Math.ceil(last / 60) * 60 + 60);
    return { startMin: from, rows: (to - from) / step };
  }, [shifts, step]);
  const quarters = Array.from({ length: rows }, (_, i) => startMin + i * QUARTER);

  return (
    <div className="sgrid-wrap">
      <div className="sgrid sgrid--day" style={{ ['--days' as string]: Math.max(people.length, 1), ['--rows' as string]: rows, ['--q' as string]: `${ROW_HEIGHT[step]}px`, ['--per-hour' as string]: 60 / step }} role="grid" aria-label="Schichtplan des Tages, eine Spalte je Mitarbeiter">
        <div className="sgrid__corner" />
        {people.length === 0 && <div className="sgrid__head"><span className="muted small">Keine Schichten</span></div>}
        {people.map((p) => {
          // Der Name zeigt beim Überfahren die Position und führt zur Mitarbeitermaske. Das Kürzel darunter wählt alle Schichten der Person aus. Beides getrennt, weil ein Link
          // nicht in einer Schaltfläche stehen darf.
          const position = positions?.get(p.id) || 'Keine Position hinterlegt';
          const tip = positions ? { 'data-tip': position } : {};
          const name = linkEmployees ? (
            <Link to={`/mitarbeiter/${p.id}`} className="sgrid__dn sgrid__name" aria-label={`${p.name}, ${position}. Mitarbeiter öffnen`} {...tip}>{p.name}</Link>
          ) : (
            <strong className="sgrid__dn sgrid__name" tabIndex={positions ? 0 : undefined} {...tip}>{p.name}</strong>
          );
          return (
            <div key={p.id} className={`sgrid__head${isToday ? ' is-today' : ''}`}>
              {name}
              {selectable ? (
                <button type="button" className="sgrid__mo sgrid__pick" onClick={() => onToggleIds?.(p.shifts.map((s) => s.id))} title="Alle Schichten des Tages auswählen" aria-label={`Alle Schichten von ${p.name} am ${day.slice(8, 10)}.${day.slice(5, 7)}. auswählen`}>{codes.get(p.id)}</button>
              ) : (
                <span className="sgrid__mo">{codes.get(p.id)}</span>
              )}
            </div>
          );
        })}

        <div className="sgrid__times" aria-hidden="true">
          {quarters.map((m) => <div key={m} className={`sgrid__time${m % 60 === 0 ? ' is-hour' : ''}`}>{label(m)}</div>)}
        </div>

        {people.length === 0 && <div className="sgrid__col" />}
        {people.map((p) => {
          const blocks = dayBlocks(p.shifts.map((s) => ({ id: s.id, allDay: false, start: `${day}T${s.startTime}`, end: `${day}T${s.endTime}` })), day);
          return (
            <div key={p.id} className={`sgrid__col${isToday ? ' is-today' : ''}`} role="row">
              {blocks.map((b) => {
                const s = p.shifts.find((x) => x.id === b.id)!;
                const on = selected?.has(s.id) ?? false;
                const full = `${s.employeeName}, ${s.startTime} bis ${s.endTime} Uhr, ${hoursDe(s.hours)}, ${SHIFT_STATUS_LABELS[s.status]}${s.note ? `, ${s.note}` : ''}${s.tasks.length > 0 ? `. Aufgaben im Dienst: ${s.tasks.map((t) => `${t.title} ${t.startTime} bis ${t.endTime} Uhr`).join('; ')}` : ''}`;
                const style = {
                  top: `calc(${(b.startMin - startMin) / QUARTER} * var(--q))`,
                  height: `calc(${(b.endMin - b.startMin) / QUARTER} * var(--q))`,
                  left: `${(b.column / b.columns) * 100}%`,
                  width: `${100 / b.columns}%`,
                  ['--hue' as string]: employeeHue(s.employeeId),
                };
                const className = `sshift sshift--${s.status}${on ? ' is-selected' : ''}${s.tasks.length > 0 ? ' has-tasks' : ''}`;
                const body = (
                  <>
                    <span className="sshift__text">{s.startTime}–{s.endTime}</span>
                    <span className="sshift__sub">{hoursDe(s.hours)}{s.breakMinutes ? `, ${s.breakMinutes} Min. Pause` : ''}</span>
                    {s.tasks.map((t) => (
                      <span
                        key={t.id}
                        className={`sshift__task${t.done ? ' is-done' : ''}`}
                        title={`${t.title}, ${t.startTime} bis ${t.endTime} Uhr`}
                        style={{ top: `calc(${(toMinutes(t.startTime) - b.startMin) / step} * var(--q))`, height: `calc(${(toMinutes(t.endTime) - toMinutes(t.startTime)) / step} * var(--q))` }}
                      >
                        <span className="sshift__task-title">{t.done ? '✓ ' : ''}{t.title}</span>
                        <span className="sshift__task-time">{t.startTime}–{t.endTime}</span>
                      </span>
                    ))}
                  </>
                );
                const menu = onMenu
                  ? (e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      const { x, y } = menuPoint(e);
                      onMenu(s, x, y);
                    }
                  : undefined;
                return selectable ? (
                  <button key={s.id} type="button" className={className} style={style} title={full} aria-label={full} aria-pressed={on} aria-haspopup={onMenu ? 'menu' : undefined} onClick={() => onToggle?.(s.id)} onContextMenu={menu}>{body}</button>
                ) : (
                  <div key={s.id} className={className} style={style} title={full} role="gridcell" aria-label={full}>{body}</div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
